import { observer } from "mobx-react";
import { useRocInternalPortalStore } from "apps/roc-internal-portal/src/app/hooks";
import { AIDocType } from "@roc/feature-documents";
import { useCallback, useMemo } from "react";
import { debounce } from "@roc/feature-utils";
import { TextField } from "@roc/ui";
import { Chip, Checkbox, Typography, makeStyles, Theme } from "@material-ui/core";
import { OutlinedFlag } from "@material-ui/icons";

const useStyles = makeStyles((theme: Theme) => ({
  iconWhite: {
    color: '#FFF'
  },
  successStyle: {
    color: theme.palette.success.dark
  },
  chipError: {
    color: '#FFF',
    backgroundColor: theme.palette.error.main,
    borderRadius: '8px',
    borderColor: 'transparent',
    width: '55px',
    "&&:hover": {
      backgroundColor: theme.palette.error.main
    },
    "&&:focus": {
      backgroundColor: theme.palette.error.main
    }
  },
  chipSuccess: {
    color: theme.palette.success.dark,
    backgroundColor: '#FFF',
    borderRadius: '8px',
    borderColor: theme.palette.success.dark,
    width: '55px',
  },
  expectedValueLabel: {
    fontWeight: 'bold',
    padding: '0 10px',
    textAlign: 'start'
  },
}));

export const ReviewTableItemValue = observer((props: { docType: AIDocType, item: any, column: any }) => {
  const { item, column, docType } = props;
  const { docReviewStore } = useRocInternalPortalStore();
  const { reviewLogStore } = docReviewStore;
  const { mapping } = column;
  const value = item[mapping];
  const classes = useStyles();

  const logEvent = useCallback(
    (value: any) => {
      reviewLogStore.addEvent({
        docType,
        mapping,
        sectionName: item.sectionName,
        itemDisplayName: item.itemDisplayName,
        value,
      });
    },
    [reviewLogStore, docType, mapping, item.sectionName, item.itemDisplayName]
  );

  const addEvent = useMemo(() => debounce(logEvent, 750), [logEvent]);

  const handlChangeDebounce = (value) => {
    docReviewStore.handleItemChange(mapping, item.sectionName, item.itemDisplayName, value);
    addEvent(value);
  }

  const handleAndLogChange = (mapping, value) => {
    docReviewStore.handleItemChange(mapping, item.sectionName, item.itemDisplayName, value);
    reviewLogStore.addEvent({ docType, mapping, sectionName: item.sectionName, itemDisplayName: item.itemDisplayName, value });
  }

  const handleNeedsRevisionFlag = (value) => {
    handleAndLogChange(mapping, value);
    if (value) {
      handleSendForRevision(value);
    }
  }

  const handleSendForRevision = (value: boolean) => {
    docReviewStore.handleItemChange('sendForRevision', item.sectionName, item.displayName, value);
    handleAndLogChange('sendForRevision', value);
    handleRevisionComment(value);
  }

  const handleRevisionComment = (value) => {
    if (value) {
      docReviewStore.setRevisionComment(docType, item)
    }
  }

  switch (mapping) {
    case 'data':
      return <TextField
        testId="data"
        variant="outlined"
        standaloneLabel
        value={value}
        fullWidth
        disabled={docReviewStore.readOnlyForm}
        onChange={e => handlChangeDebounce(e.target.value)}
      />
    case 'reviewerComment':
      return <TextField
        testId="reviewerComment"
        variant="outlined"
        standaloneLabel
        value={value}
        multiline
        maxRows={4}
        fullWidth
        disabled={docReviewStore.readOnlyForm}
        onChange={e => docReviewStore.handleItemChange(mapping, item.sectionName, item.itemDisplayName, e.target.value)}
      />
    case 'needsRevisionFlag':
      const className = !!value ? classes.chipError : classes.chipSuccess;
      return <Chip
        variant="outlined"
        size="small"
        icon={<OutlinedFlag className={!!value ? classes.iconWhite : classes.successStyle} />}
        label={!!value ? 'Yes' : 'No'}
        className={className}
        style={{
          fontWeight: 0,
        }}
        clickable={!docReviewStore.readOnlyForm}
        onClick={(e) => {
          if (!docReviewStore.readOnlyForm) {
            handleNeedsRevisionFlag(!value);
          }
        }}
      />
    case 'sendForRevision':
      return <Checkbox
        checked={value}
        size='small'
        disabled={docReviewStore.readOnlyForm}
        onChange={e => handleSendForRevision(e.target.checked)}
        color="secondary"
      />
    case 'expectedData':
      return <Typography variant="body2" className={classes.expectedValueLabel}>{value}</Typography>
    default:
      return <Typography variant="body2">{value}</Typography>
  }
});